import type { User } from './types';
import { LoginService } from './service';
import type { ApiErrorResponse } from '@/types/api';
import { setDisplayLocale } from '@/utils/format';

let cachedUser: User | null = null;
let cachedUserFetchedAt = 0;
let inflightRequest: Promise<User> | null = null;
let cacheGeneration = 0;

/**
 * How long a fetched user stays fresh. Access gates read through this cache on
 * every navigation, so the window is what bounds how stale a license or plan
 * projection can look in the UI — the backend stays authoritative for every
 * actual spend or mutation. Known changes (company switch, license edits,
 * password change) bypass the window entirely via notifyUserUpdated.
 */
export const USER_CACHE_TTL_MS = 30_000;

function normalizeUserPayload(user: User): User {
  return {
    ...user,
    licenseId: user.licenseId ?? null,
    effectiveFeatureGroups: user.effectiveFeatureGroups ?? [],
    seatLimits: user.seatLimits ?? [],
    trialState: user.trialState ?? null,
  };
}

function commitCachedUser(user: User): void {
  cachedUser = user;
  cachedUserFetchedAt = Date.now();
  // Dates and money render in the reader's own language from here on. This is
  // the one place the whole app learns which locale that is, so it sits with
  // the accepted session payload rather than being threaded through every
  // formatting call. Superseded responses never reach here, so a stale
  // request cannot flip the locale either.
  setDisplayLocale(user.languageCode);
}

/**
 * Retrieve the current authenticated user with an in-memory cache.
 * Concurrent callers share one pending request. `forceRefresh` guarantees the
 * answer comes from the server, not the cached value — but it still joins a
 * request that is already in flight, because that request is fetching the
 * current server state anyway and a parallel duplicate would add nothing.
 * Invalidation after a session change goes through clearCachedUserCache,
 * which is the operation that abandons in-flight responses.
 */
export async function getCachedUser(options: { forceRefresh?: boolean } = {}): Promise<User> {
  const { forceRefresh = false } = options;

  const cacheExpired = Date.now() - cachedUserFetchedAt > USER_CACHE_TTL_MS;
  if (cachedUser && !forceRefresh && !cacheExpired) {
    return cachedUser;
  }
  cachedUser = null;

  if (inflightRequest) {
    return inflightRequest;
  }

  const requestGeneration = cacheGeneration;
  const request: Promise<User> = LoginService.getCurrentUser()
    .then(normalizeUserPayload)
    .then((user) => {
      if (requestGeneration === cacheGeneration) {
        commitCachedUser(user);
      }
      return user;
    })
    .finally(() => {
      if (inflightRequest === request) {
        inflightRequest = null;
      }
    });
  inflightRequest = request;

  return request;
}

/**
 * Monotonic token identifying the current session generation. A caller that
 * acts on a request's FAILURE (not just its value) captures this before the
 * request and compares after: a changed generation means the failure belongs
 * to a session that no longer exists — logout, a new login, a company switch
 * — and acting on it would tear down the session that replaced it.
 */
export function getUserCacheGeneration(): number {
  return cacheGeneration;
}

/**
 * Invalidate the cache after a session change (logout, company switch, user
 * mutation). Bumping the generation makes any in-flight response unable to
 * populate the cache: it was issued under the previous session state.
 */
export function clearCachedUserCache(): void {
  cacheGeneration += 1;
  cachedUser = null;
  cachedUserFetchedAt = 0;
  inflightRequest = null;
}

/**
 * Prime the in-memory cache with a trusted user payload, such as a fresh login
 * or completed-MFA response. The payload supersedes anything already in
 * flight: an older request finishing after this must not overwrite the user
 * who just authenticated.
 */
export function primeCachedUser(user: User): void {
  cacheGeneration += 1;
  inflightRequest = null;
  commitCachedUser(normalizeUserPayload(user));
}

/**
 * Notify all components that user data has been updated.
 * This will trigger a refresh in components listening for the 'user-updated' event.
 */
export function notifyUserUpdated(): void {
  clearCachedUserCache();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('user-updated'));
  }
}

export function refreshCachedUserAfterAccessDenied(
  statusCode: number,
  response: ApiErrorResponse,
): void {
  if (statusCode !== 403) {
    return;
  }

  const ref = response.details?.ref || (typeof response.detail === 'object' ? response.detail.ref : undefined);
  const accessRevision = response.details?.data?.accessRevision
    || (typeof response.detail === 'object' ? response.detail.data?.accessRevision : undefined);
  if (
    accessRevision !== undefined
    || ref === 'feature_crm'
    || ref === 'feature_company_admin'
    || ref === 'company_crm'
    || ref === 'deletion'
    || ref === 'subscription'
    || ref === 'plan'
  ) {
    notifyUserUpdated();
  }
}

export function getAuthErrorStatusCode(error: unknown): number | undefined {
  if (typeof error === 'object' && error && 'statusCode' in error) {
    const statusCode = (error as { statusCode?: unknown }).statusCode;
    return typeof statusCode === 'number' ? statusCode : undefined;
  }
  return undefined;
}

export function getAuthErrorRef(error: unknown): string | undefined {
  if (typeof error !== 'object' || !error || !('response' in error)) {
    return undefined;
  }

  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || !response) {
    return undefined;
  }

  if ('details' in response) {
    const details = (response as { details?: unknown }).details;
    if (typeof details === 'object' && details && 'ref' in details) {
      const ref = (details as { ref?: unknown }).ref;
      if (typeof ref === 'string' && ref.length > 0) {
        return ref;
      }
    }
  }

  if ('detail' in response) {
    const detail = (response as { detail?: unknown }).detail;
    if (typeof detail === 'object' && detail && 'ref' in detail) {
      const ref = (detail as { ref?: unknown }).ref;
      if (typeof ref === 'string' && ref.length > 0) {
        return ref;
      }
    }
  }

  return undefined;
}

export function isAuthSessionError(error: unknown): boolean {
  const statusCode = getAuthErrorStatusCode(error);
  const errorRef = getAuthErrorRef(error);
  return (
    statusCode === 401
    || errorRef === 'auth'
    || errorRef === 'auth_revoked'
    || errorRef === 'user_status'
  );
}

/**
 * Get the current user from cache synchronously.
 * Returns null if user is not yet loaded.
 * Use this for immediate access in components that render after authentication.
 */
export function getCurrentUser(): User | null {
  return cachedUser;
}
